import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { HitAvatar, avatarBus, nextFx, avatarById } from "@/components/avatar/HitAvatar";

export const RANK_META = {
  common: { label: "Común", color: "#a3a3a3", grad: "linear-gradient(90deg,#6f6f6f,#a3a3a3)" },
  uncommon: { label: "Poco común", color: "#34D399", grad: "linear-gradient(90deg,#059669,#34D399,#a7f3d0)" },
  rare: { label: "Rara", color: "#f7d774", grad: "linear-gradient(90deg,#7a5410,#f7d774,#fff3c4)" },
  holo: { label: "Holo", color: "#4cc9f0", grad: "linear-gradient(90deg,#ff4d6d,#ffd166,#06d6a0,#4cc9f0,#b388ff)" },
  secret: { label: "Secret Rare", color: "#f472b6", grad: "linear-gradient(90deg,#fde68a,#f472b6,#fff,#c084fc)" },
};
export const RANK_ORDER = [["common", 0], ["uncommon", 5], ["rare", 25], ["holo", 75], ["secret", 200]];
export const frameOf = (u) => u?.frame || u?.rank || "common";
const SEEN_KEY = "hittcg:rank-seen";

export const RankName = ({ rank, className = "" }) => {
  const m = RANK_META[rank] || RANK_META.common;
  return <span className={`sx-mono text-[10px] font-bold uppercase tracking-[.14em] ${className}`} style={{ color: m.color }} data-testid="rank-name">{m.label}</span>;
};

const HOW = [
  { k: "+1", color: "#34D399", border: "rgba(255,255,255,.1)", t: "Cada compra o venta entregada", d: "Cuenta cuando el pedido se entrega o la subasta se paga y se entrega." },
  { k: "×2", color: "#FF4438", border: "rgba(255,68,56,.55)", t: "Buena reseña, doble puntaje", d: "Si la otra persona te califica con 4 o 5★, esa transacción vale 2 puntos." },
  { k: "=1", color: "#ffffff", border: "rgba(255,255,255,.1)", t: "Un solo perfil", d: "Lo que compras y lo que vendes suma al mismo rango y al mismo marco de tu avatar." },
];

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("es-EC", { day: "2-digit", month: "short" }); } catch { return ""; } };

const RankUpToast = ({ rank, onDone }) => {
  const m = RANK_META[rank] || RANK_META.common;
  useEffect(() => { const t = setTimeout(onDone, 2800); return () => clearTimeout(t); }, [onDone]);
  return createPortal(
    <div role="status" aria-live="polite" data-testid="rank-up-toast"
      className="sx fixed z-[95] top-[76px] left-4 right-4 md:left-auto md:right-6 md:w-[340px] flex items-center gap-3 px-4 py-3.5 bg-[#121212] rounded-[18px] border hit-rank-toast"
      style={{ borderColor: m.color, boxShadow: `0 0 24px ${m.color}8c` }}>
      <span aria-hidden className="text-2xl leading-none" style={{ color: m.color }}>★</span>
      <div className="flex flex-col gap-0.5">
        <span className="sx-mono text-[10px] font-bold tracking-[.14em]" style={{ color: m.color }}>¡SUBISTE DE RANGO!</span>
        <span className="text-base font-extrabold text-white">Ahora eres {m.label}</span>
      </div>
    </div>,
    document.body,
  );
};

const ProgressBar = ({ data }) => {
  const span = data.next_min != null ? data.next_min - data.current_min : 1;
  const pct = data.next_min != null ? Math.min(100, Math.max(0, ((data.points - data.current_min) / span) * 100)) : 100;
  const [w, setW] = useState(0);
  useEffect(() => { const t = requestAnimationFrame(() => setW(pct)); return () => cancelAnimationFrame(t); }, [pct]);
  const next = RANK_META[data.next_rank] || RANK_META[data.rank];
  const cur = RANK_META[data.rank] || RANK_META.common;
  return (
    <div className="space-y-2">
      <div role="progressbar" aria-valuemin={data.current_min} aria-valuemax={data.next_min ?? data.points} aria-valuenow={data.points}
        aria-label="Progreso de rango" data-testid="rank-progress" className="relative h-[14px] rounded-full bg-white/[.06] border border-white/10 overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-[900ms] ease-[cubic-bezier(.34,1.56,.64,1)]" style={{ width: `${w}%`, background: next.grad }} data-testid="rank-progress-fill" />
        <div aria-hidden className="absolute inset-0 grid grid-cols-10">{Array.from({ length: 10 }, (_, i) => <span key={i} className={i ? "border-l border-black/50" : ""} />)}</div>
      </div>
      <div className="flex flex-wrap sm:flex-nowrap justify-between items-start gap-x-2 gap-y-1.5 sx-mono text-[9px] tracking-[.12em] uppercase text-white/45">
        <span className="whitespace-nowrap" data-testid="rank-bar-min">{cur.label} · {data.current_min}</span>
        <span className="order-last sm:order-none w-full sm:w-auto text-[11px] normal-case tracking-normal text-white/70 font-sans text-center" data-testid="rank-missing">
          {data.next_min != null ? <>{data.next_min - data.points === 1 ? "Falta" : "Faltan"} <b className="text-white">{data.next_min - data.points} {data.next_min - data.points === 1 ? "pt" : "pts"}</b> para <b style={{ color: next.color }}>{next.label}</b></> : "Rango máximo alcanzado"}
        </span>
        <span className="whitespace-nowrap" data-testid="rank-bar-max">{data.next_rank ? `${next.label} · ${data.next_min}` : ""}</span>
      </div>
    </div>
  );
};

const Ladder = ({ rank }) => {
  const idx = RANK_ORDER.findIndex(([k]) => k === rank);
  return (
    <ol className="grid grid-cols-5 gap-1.5" data-testid="rank-ladder">
      {RANK_ORDER.map(([k, min], i) => {
        const m = RANK_META[k], done = i <= idx, cur = i === idx;
        return (
          <li key={k} data-testid={`rank-step-${k}`} className={`rounded-[12px] border px-1.5 py-2 text-center ${cur ? "bg-white/[.05]" : done ? "border-white/10" : ""}`}
            style={cur ? { borderColor: m.color, boxShadow: `0 0 18px ${m.color}55` } : done ? undefined : { borderColor: "rgba(255,255,255,.14)" }}>
            <div className="text-[11px] font-bold leading-tight" style={{ color: done ? m.color : "rgba(255,255,255,.6)" }}>{done ? "✓ " : ""}{m.label}</div>
            <div className={`sx-mono text-[9px] mt-0.5 ${done ? "text-white/40" : "text-white/60"}`}>{min} pts</div>
          </li>
        );
      })}
    </ol>
  );
};

// Rango del usuario autenticado + toast de subida (una vez por rango visto).
export const useMyRank = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [fx, setFx] = useState("");
  const [upRank, setUpRank] = useState(null);
  useEffect(() => {
    if (!user) { return undefined; }
    let live = true;
    api.get("/me/rank").then(({ data: d }) => {
      if (!live) return;
      setData(d);
      const seen = localStorage.getItem(`${SEEN_KEY}:${user.id}`);
      localStorage.setItem(`${SEEN_KEY}:${user.id}`, d.rank);
      const up = seen && RANK_ORDER.findIndex(([k]) => k === d.rank) > RANK_ORDER.findIndex(([k]) => k === seen);
      if (up) {
        setFx(nextFx("notify"));
        avatarBus.notify();
        setUpRank(d.rank);
      }
    }).catch(() => {});
    return () => { live = false; };
  }, [user]);
  const rank = data?.rank || user?.rank || "common";
  const toastEl = upRank ? <RankUpToast rank={upRank} onDone={() => setUpRank(null)} /> : null;
  return { data, rank, fx, toastEl };
};

// Ficha superior del perfil propio: avatar con marco + identidad + rango, barra y escalera.
export const RankProfileHeader = ({ user, identity, avatarTestid }) => {
  const { data, rank, fx, toastEl } = useMyRank();
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-4 md:gap-x-6 gap-y-5" data-testid="rank-panel">
      {toastEl}
      <div className="md:row-span-2"><HitAvatar avatarId={user?.avatar_id} name={user?.name} size={104} rank={data?.frame || frameOf(user)} fx={fx} testid={avatarTestid} /></div>
      <div className="min-w-0 self-center md:self-start">{identity}</div>
      <div className="col-span-2 md:col-span-1 min-w-0"><RankSummary data={data} rank={rank} /></div>
    </div>
  );
};

export const RankSummary = ({ data, rank }) => {
  const m = RANK_META[rank] || RANK_META.common;
  return (
      <div className="space-y-4 min-w-0" data-testid="rank-summary">
        <div className="sx">
          <div className="sx-mono text-[10px] tracking-[.14em] text-white/50">TU RANGO</div>
          <div className="flex items-end justify-between gap-3">
            <div className="text-[clamp(26px,8vw,34px)] md:text-[40px] whitespace-nowrap font-extrabold tracking-[-.04em] leading-[1.05]" style={{ color: m.color }} data-testid="rank-name-big">{m.label}</div>
            <div className="flex items-baseline gap-1.5 shrink-0 pb-1" data-testid="rank-points"><span className="sx-mono text-[28px] font-bold leading-none">{data?.points ?? "—"}</span><span className="sx-mono text-[11px] text-white/50">PTS</span></div>
          </div>
        </div>
        {data && <ProgressBar data={data} />}
        <Ladder rank={rank} />
      </div>
  );
};

export const useRankData = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!user) { return undefined; }
    let live = true;
    api.get("/me/rank").then(({ data: d }) => { if (live) setData(d); }).catch(() => {});
    return () => { live = false; };
  }, [user]);
  return data;
};

const STATES = [
  ["activo", "Activo", "Tu avatar normal mientras navegas, compras o vendes."],
  ["aburrido", "Aburrido", "Si pasas 2 minutos sin moverte en la página. Despierta al tocar, escribir o hacer scroll."],
  ["pensativo", "Pensativo", "Mientras escribes el monto de una puja o buscas y filtras cartas."],
  ["notificacion", "Notificación", "Salta con un «!» cuando te llega una notificación nueva o subes de rango."],
  ["puja", "Puja", "Celebra con el martillo al enviar una puja; así aparece en el historial de la subasta."],
];

export const AvatarStates = ({ avatarId }) => {
  const id = avatarById(avatarId)?.id;
  return (
    <div className="space-y-2" data-testid="avatar-states">
      <div className="sx-mono text-[10px] tracking-[.14em] text-white/50 uppercase">Estados de tu avatar</div>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        {STATES.map(([k, t, d]) => (
          <div key={k} className="flex sm:flex-col items-center sm:text-center gap-3 sm:gap-2 p-3 rounded-[14px] bg-white/[.03] border border-white/10" data-testid={`avatar-state-${k}`}>
            {id ? <img src={`/avatars/${id}/${k}.webp`} alt={t} className="w-14 h-14 rounded-full shrink-0" /> : <span className="w-14 h-14 rounded-full bg-white/10 shrink-0" />}
            <span className="flex flex-col gap-1 min-w-0"><b className="text-sm text-white">{t}</b><span className="text-xs text-white/65 leading-relaxed">{d}</span></span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const RankHowBody = ({ data }) => (
  <>
      <div className="grid sm:grid-cols-3 gap-2" data-testid="rank-how">
        <div className="sm:col-span-3 sx-mono text-[10px] tracking-[.14em] text-white/50 uppercase">Cómo funciona</div>
        {HOW.map((h) => (
          <div key={h.k} className="sx flex gap-3 items-start p-3.5 rounded-[14px] bg-white/[.03] border" style={{ borderColor: h.border }} data-testid={`rank-how-${h.k}`}>
            <span className="sx-mono text-lg font-extrabold shrink-0 leading-none mt-0.5" style={{ color: h.color }}>{h.k}</span>
            <span className="flex flex-col gap-1"><b className="text-sm text-white">{h.t}</b><span className="text-xs text-white/65 leading-relaxed">{h.d}</span></span>
          </div>
        ))}
        <div className="sm:col-span-3 text-xs text-white/50">Las reseñas de 1 a 3★ no restan. Reembolsos y cancelaciones no suman.</div>
      </div>
      <div className="space-y-2" data-testid="rank-recent">
        <div className="sx-mono text-[10px] tracking-[.14em] text-white/50 uppercase">Últimos puntos</div>
        {data?.recent?.length ? data.recent.map((r, i) => (
          <div key={i} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-white/5" data-testid={`rank-recent-${i}`}>
            <span className="text-white/80 truncate">{r.text}</span>
            <span className="flex items-center gap-2.5 shrink-0"><span className="sx-mono text-[10px] text-white/45">{fmtDate(r.created_at)}</span><span className="sx-mono font-bold text-[#34D399]">+{r.points}</span></span>
          </div>
        )) : <div className="text-xs text-white/45">Aún no tienes puntos. Completa tu primera compra o venta.</div>}
      </div>
  </>
);

// Tarjeta propia justo después de la ficha: cómo funciona + últimos puntos + estados.
export const RankDetails = () => {
  const { user } = useAuth();
  const data = useRankData();
  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-6 space-y-5" data-testid="rank-details-card">
      <RankHowBody data={data} />
      <AvatarStates avatarId={user?.avatar_id} />
    </div>
  );
};

export const ChangeAvatarButton = ({ testid }) => (
  <button type="button" onClick={() => avatarBus.openPicker("cambiar")} data-testid={testid} className="mt-2 h-9 px-4 rounded-full border border-white/15 text-sm font-semibold text-white/85 hover:border-[#FF4438] hover:text-white transition-colors">Cambiar avatar</button>
);
